import { useEffect, useState, useMemo, useCallback } from 'react';
import { Grid, TextField, Button, Select, MenuItem, FormControl, InputLabel, Box, Typography } from '@mui/material';
import { adminListColumns } from '../../component/common/tableHelper';
import useApiService from '../../component/hooks/useApiService';
import Filter from '../../component/ui/filter/filter';
import { useDialog } from '../../context/dialog/DialogContext';
import AddIcon from '@mui/icons-material/Add';
import { useAppSnackbar } from '../../component/providers/SnackbarProvider';
import AddAdmin from './AddAdmin';
import DataGrid from '../../component/ui/data-grid/DataGrid';
import { ADMIN_TYPES } from '../../constans/parameter';
import { AdminAndUserStatusActions } from '../../constans/enum';
import { apiUrls } from '../../constans/apiUrls';


const AdminList = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { executeGet } = useApiService();
  const { openDialog } = useDialog();
  const { showSnackbar } = useAppSnackbar();

  const columns = useMemo(() => adminListColumns(), []);

  const fetchData = async (filterParams = filter, page = paginationModel.page, pageSize = paginationModel.pageSize) => {
    setLoading(true);
    try {
      const response = await executeGet({
        url: apiUrls.Admin,
        params: {
          ...filterParams,
          offset: page * pageSize,
          limit: pageSize,
        },
      });

      setRows(response.items ?? []);
      setTotalCount(response.meta?.total ?? 0);
    } catch (err) {
      console.error('Veri alınamadı:', err);
      setError('Veri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filter, paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleReset = () => {
    const emptyFilter = {};
    setFilter(emptyFilter);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    if (paginationModel.page === 0) {
      fetchData({}, 0);
    }
  };

  const handleClick = () => {
    openDialog({
      title: 'Yeni Admin Ekle',
      maxWidth: 'sm',
      content: <AddAdmin openType="Create" />,
      callback: (response) => {
        if (response.admin) {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          fetchData(filter, 0, paginationModel.pageSize);
          showSnackbar('Admin eklendi.', 'info');
        }
      },
    });
  };

  const onChangeRequestClicked = useCallback(
    (data) => {
      openDialog({
        title: "Admin Bilgisi Güncelleme",
        maxWidth: 'sm',
        content: <AddAdmin data={data} openType={"Update"} adminPanelAction={AdminAndUserStatusActions.CHANGE_REQUESTED} />,
        callback: (response) => {
          if (response === true) {
            fetchData();
            showSnackbar(`Admin'in bilgisi güncellendi.`, 'info');
          }
          else if (response === false) {
            showSnackbar(`Admin'in bilgisi güncellenemedi.`, 'error');
          }
        },
      });
    },
    [filter]
  );

  const onChangePasswordClicked = useCallback(
    (data) => {
      openDialog({
        title: "Şifreyi Güncelle",
        maxWidth: 'sm',
        content: <AddAdmin data={data} openType={"PasswordUpdate"} adminPanelAction={AdminAndUserStatusActions.CHANGE_PASSWORD} />,
        callback: (response) => {
          if (response === true) {
            fetchData();
            showSnackbar(`Admin'in şifresi güncellendi.`, 'info');
          }
          else if (response === false) {
            showSnackbar(`Admin'in şifresi güncellenemedi.`, 'error');
          }
        },
      });
    },
    [filter]
  );

  const onViewClicked = useCallback(
    (data) => {
      openDialog({
        title: "Admin Bilgisi",
        maxWidth: 'sm',
        content: <AddAdmin openType={"View"} data={data} />,
        callback: () => { fetchData(filter) },
      });
    },
    [filter]);

  const onCancelClicked = useCallback(
    (data) => {
      const ConfirmContent = ({ close }) => (
        <Box>
          <Typography gutterBottom>
            Admin <b>silinecektir</b>. Onaylıyor musunuz?
          </Typography>
          <Typography variant="body2" color="warning.main" gutterBottom>
            Not: Admin silinince geri alınamayacaktır.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={() => close(false)}>Vazgeç</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                close(true);
                openDialog({
                  title: "Admin Silme",
                  maxWidth: "sm",
                  content: <AddAdmin openType={"Delete"} data={data} adminPanelAction={AdminAndUserStatusActions.DELETE_ADMIN} />,
                  callback: (response) => {
                    if (response === true) {
                      fetchData();
                      showSnackbar('Admin silindi.', 'info');
                    } else if (response === false) {
                      showSnackbar('Admin silinemedi.', 'error');
                    }
                  },
                });
              }}
            >
              Onayla
            </Button>
          </Box>
        </Box>
      );
      openDialog({ title: 'Onay', maxWidth: 'xs', content: <ConfirmContent /> });
    },
    [filter]
  );


  const rowActions = useMemo(
    () => [
      {
        name: 'view',
        icon: 'view',
        tooltip: "Görüntüle",
        onClick: onViewClicked
      },
      {
        name: 'changeRequest',
        icon: "edit",
        tooltip: "Düzenle",
        onClick: onChangeRequestClicked,
      },
      {
        name: 'PasswordUpdate',
        icon: "editPassword",
        tooltip: "Şifreyi Güncelle",
        onClick: onChangePasswordClicked,
      },
      {
        name: 'cancelLetterCredit',
        icon: "cancel",
        tooltip: "Sil",
        onClick: onCancelClicked,
      },
    ],
    [filter]
  );

  const onRowActionVisible = (row, action) => {
    switch (action) {
      case 'view':
        return true;
      case 'changeRequest':
        return true;
      case 'cancelLetterCredit':
        return true;
      case 'PasswordUpdate':
        return true;
      default:
        return false;
    }
  };


  return (
    <div style={{ padding: '0 1rem 1rem' }}>
      <h2>Admin Listesi</h2>
      {error && <div style={{ color: 'red' }}>{error}</div>}

      <Filter onFilter={() => fetchData(filter)} onFilterReset={handleReset}
        rightAction={(
          <Button
            onClick={handleClick}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              backgroundColor: '#1976d2',
              color: 'white',
              borderRadius: '10px',
              fontWeight: 'bold',
            }}
          >
            Yeni Admin Ekle
          </Button>
        )}
      >
        <Grid item xs={4}>
          <TextField
            fullWidth
            name="Email"
            label="Email"
            variant="outlined"
            value={filter?.q || ''}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl sx={{ minWidth: 220 }} variant="outlined">
            <InputLabel id="admin-type-label">Rol</InputLabel>
            <Select
              labelId="admin-type-label"
              id="admin-type"
              value={filter?.role || ''}
              onChange={(e) => setFilter({ ...filter, role: e.target.value })}
              label="Rol"
            >
              {ADMIN_TYPES.map((type) => (
                <MenuItem key={type.Code} value={type.Code}>
                  {type.ValueText1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Filter>

      <div style={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          rowActions={rowActions}
          onRowActionVisible={onRowActionVisible}
          //checkboxSelection
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          //onRowSelectionModelChange={(newSelection) => {
          //const selectedRow = rows.find((row) => (row.id ?? row.Id) === newSelection[0]);
          //setRowSelectionModel(selectedRow || "")}}
          // rowSelectionModel={rowSelectionModel.id}
          loading={loading}
          getRowId={(row, index) => row.id ?? index + 1}
          onRowSelect={(d) => { console.log("satur", d) }}
        />
      </div>
    </div>
  );
};

export default AdminList;
